import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoriesApi, emailApi, billsApi, adminApi, schedulerApi, webhooksApi, householdsApi, remindersApi } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  Trash2,
  User,
  Users,
  FolderOpen,
  LogOut,
  Mail,
  RefreshCw,
  Check,
  X,
  AlertCircle,
  CheckCircle2,
  Pencil,
  Shield,
  Clock,
  UserCheck,
  UserX,
  Timer,
  Play,
  CreditCard,
  ExternalLink,
  Webhook,
  Send,
  Bell,
} from 'lucide-react'
import { Category, Bill, ReminderPreferences } from '@/types'
import { HouseholdSettings } from '@/components/HouseholdSettings'
import { Home } from 'lucide-react'
import { CategoryIcon } from '@/lib/category-icon'

const COLORS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#84CC16',
]

const ICONS = [
  'zap', 'flame', 'droplets', 'wifi', 'smartphone', 'home',
  'car', 'shopping-cart', 'utensils', 'heart', 'film', 'plane',
  'gift', 'book', 'briefcase', 'creditcard', 'dollar-sign',
]

interface EmailConfig {
  id?: number
  imap_host: string
  imap_port: number
  imap_user: string
  has_password?: boolean
  last_check?: string
  active: number
  configured?: boolean
  // OAuth fields
  oauth_provider?: string
  oauth_email?: string
  oauth_connected?: boolean
  oauth_expires_at?: string
}

interface EmailParser {
  id: number
  bill_id: number
  bill_name: string
  name: string
  sender_pattern: string
  amount_pattern?: string
  due_date_pattern?: string
  subject_pattern?: string
  template: string
  folder?: string
  active: number
}

interface ParsedEmail {
  id: number
  email_parser_id: number
  bill_id: number
  bill_name: string
  message_id: string
  received_at: string
  sender: string
  subject: string
  extracted_amount?: number
  extracted_due_date?: string
  extracted_payment_link?: string
  status: string
}

interface ParserTemplate {
  id: string
  name: string
  amount_pattern: string
  due_date_pattern: string
}

interface AdminUser {
  id: number
  name: string
  email: string
  role: string
  status: string
  created_at: string
}

interface SchedulerJobStatus {
  enabled: boolean
  interval: number
  running: boolean
  lastRun: {
    time: string | null
    success: boolean | null
    error: string | null
    result: any
  } | null
}

interface SchedulerStatus {
  email: SchedulerJobStatus
  transactions: SchedulerJobStatus
}

interface Household {
  id: number
  name: string
  role: string
  invite_code: string
}

export default function Settings() {
  const { user, logout } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // Fetch current household to get role
  const { data: household } = useQuery<Household | null>({
    queryKey: ['household'],
    queryFn: async () => {
      const res = await householdsApi.getMine()
      return res.data
    },
  })

  // Check if user is household admin
  const isHouseholdAdmin = household?.role === 'admin'
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createType, setCreateType] = useState<'bill' | 'expense'>('bill')
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [isParserOpen, setIsParserOpen] = useState(false)
  const [editingParser, setEditingParser] = useState<EmailParser | null>(null)
  const [isCreatingBill, setIsCreatingBill] = useState(false)
  const [newBillName, setNewBillName] = useState('')
  const [selectedBillId, setSelectedBillId] = useState<string>('')
  const [oauthMessage, setOauthMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Handle OAuth callback parameters
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success')
    const oauthError = searchParams.get('oauth_error')
    const oauthEmail = searchParams.get('email')

    if (oauthSuccess) {
      setOauthMessage({ type: 'success', text: `Connected to Microsoft as ${oauthEmail || 'your account'}` })
      queryClient.invalidateQueries({ queryKey: ['email', 'config'] })
      // Clear URL params
      setSearchParams({})
    } else if (oauthError) {
      setOauthMessage({ type: 'error', text: `OAuth failed: ${oauthError}` })
      setSearchParams({})
    }
  }, [searchParams, setSearchParams, queryClient])

  // Category queries
  const { data: billCategories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'bill'],
    queryFn: async () => {
      const res = await categoriesApi.getAll('bill')
      return res.data
    },
  })

  const { data: expenseCategories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'expense'],
    queryFn: async () => {
      const res = await categoriesApi.getAll('expense')
      return res.data
    },
  })

  // Email queries
  const { data: emailConfig } = useQuery<EmailConfig>({
    queryKey: ['email', 'config'],
    queryFn: async () => {
      const res = await emailApi.getConfig()
      return res.data
    },
  })

  const { data: emailTemplates = [] } = useQuery<ParserTemplate[]>({
    queryKey: ['email', 'templates'],
    queryFn: async () => {
      const res = await emailApi.getTemplates()
      return res.data
    },
  })

  const { data: emailParsers = [] } = useQuery<EmailParser[]>({
    queryKey: ['email', 'parsers'],
    queryFn: async () => {
      const res = await emailApi.getParsers()
      return res.data
    },
  })

  const { data: parsedEmails = [] } = useQuery<ParsedEmail[]>({
    queryKey: ['email', 'parsed'],
    queryFn: async () => {
      const res = await emailApi.getParsedEmails()
      return res.data
    },
  })

  const { data: bills = [] } = useQuery<Bill[]>({
    queryKey: ['bills'],
    queryFn: async () => {
      const res = await billsApi.getAll()
      return res.data
    },
  })

  // Admin queries (only if user is admin)
  const { data: allUsers = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await adminApi.getUsers()
      return res.data
    },
    enabled: user?.role === 'admin',
  })

  const { data: pendingUsers = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin', 'pending-users'],
    queryFn: async () => {
      const res = await adminApi.getPendingUsers()
      return res.data
    },
    enabled: user?.role === 'admin',
  })

  // Scheduler query
  const { data: schedulerStatus } = useQuery<SchedulerStatus>({
    queryKey: ['scheduler', 'status'],
    queryFn: async () => {
      const res = await schedulerApi.getStatus()
      return res.data
    },
    refetchInterval: 60000, // Refresh every minute
  })

  // Mutations
  const createCategory = useMutation({
    mutationFn: categoriesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setIsCreateOpen(false)
    },
  })

  const deleteCategory = useMutation({
    mutationFn: categoriesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })

  const updateCategory = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; icon?: string; color?: string; budget_amount?: number } }) =>
      categoriesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      setIsEditCategoryOpen(false)
      setEditingCategory(null)
    },
  })

  const updateEmailConfig = useMutation({
    mutationFn: emailApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'config'] })
    },
    onError: (error) => {
      console.error('Email config error:', error)
    },
  })

  const testEmailConnection = useMutation({
    mutationFn: emailApi.testConnection,
  })

  const disconnectOAuth = useMutation({
    mutationFn: emailApi.disconnectOAuth,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'config'] })
      setOauthMessage({ type: 'success', text: 'Microsoft account disconnected' })
    },
  })

  const checkEmails = useMutation({
    mutationFn: emailApi.checkEmails,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsed'] })
      queryClient.invalidateQueries({ queryKey: ['email', 'config'] })
    },
  })

  const createParser = useMutation({
    mutationFn: emailApi.createParser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsers'] })
      setIsParserOpen(false)
      setSelectedBillId('')
      setIsCreatingBill(false)
      setNewBillName('')
    },
  })

  const updateParser = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<any> }) =>
      emailApi.updateParser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsers'] })
      setIsParserOpen(false)
      setEditingParser(null)
      setSelectedBillId('')
    },
  })

  const createBillForParser = useMutation({
    mutationFn: billsApi.create,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      setSelectedBillId(response.data.id.toString())
      setIsCreatingBill(false)
      setNewBillName('')
    },
  })

  const deleteParser = useMutation({
    mutationFn: emailApi.deleteParser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsers'] })
    },
  })

  const applyParsedEmail = useMutation({
    mutationFn: emailApi.applyParsedEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsed'] })
      queryClient.invalidateQueries({ queryKey: ['bills'] })
    },
  })

  const ignoreParsedEmail = useMutation({
    mutationFn: emailApi.ignoreParsedEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsed'] })
    },
  })

  const deleteParsedEmail = useMutation({
    mutationFn: emailApi.deleteParsedEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', 'parsed'] })
    },
  })

  // Admin mutations
  const approveUser = useMutation({
    mutationFn: adminApi.approveUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  const rejectUser = useMutation({
    mutationFn: adminApi.rejectUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  const deleteUser = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  // Scheduler mutations
  const updateScheduler = useMutation({
    mutationFn: ({ type, enabled, interval }: { type: 'email' | 'transactions'; enabled: boolean; interval?: number }) =>
      schedulerApi.update(type, { enabled, interval }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler'] })
    },
  })

  const runSchedulerJob = useMutation({
    mutationFn: schedulerApi.runNow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler'] })
      // Also refresh related data
      queryClient.invalidateQueries({ queryKey: ['email', 'parsed'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
  })

  const { data: reminderPrefs, isLoading: remindersLoading } = useQuery<ReminderPreferences>({
    queryKey: ['reminder-preferences'],
    queryFn: async () => {
      const res = await remindersApi.getPreferences()
      return res.data
    },
  })

  const updateReminderPrefsMutation = useMutation({
    mutationFn: (data: Partial<Pick<ReminderPreferences, 'enabled' | 'days_before' | 'send_overdue'>>) =>
      remindersApi.updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminder-preferences'] })
    },
  })

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    createCategory.mutate({
      name: formData.get('name') as string,
      icon: formData.get('icon') as string || undefined,
      color: formData.get('color') as string || undefined,
      type: createType,
      budget_amount: formData.get('budget_amount')
        ? parseFloat(formData.get('budget_amount') as string)
        : undefined,
    })
  }

  const handleEditCategorySubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editingCategory) return
    const formData = new FormData(e.currentTarget)
    const icon = formData.get('icon') as string
    const color = formData.get('color') as string
    const budgetStr = formData.get('budget_amount') as string
    updateCategory.mutate({
      id: editingCategory.id,
      data: {
        name: formData.get('name') as string,
        icon: icon || undefined,
        color: color || undefined,
        budget_amount: budgetStr ? parseFloat(budgetStr) : undefined,
      },
    })
  }

  const openEditCategory = (category: Category) => {
    setEditingCategory(category)
    setIsEditCategoryOpen(true)
  }

  const handleEmailConfigSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    updateEmailConfig.mutate({
      imap_host: formData.get('imap_host') as string,
      imap_port: parseInt(formData.get('imap_port') as string) || 993,
      imap_user: formData.get('imap_user') as string,
      imap_password: formData.get('imap_password') as string || undefined,
      active: true,
    })
  }

  const handleParserSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      bill_id: parseInt(selectedBillId || formData.get('bill_id') as string),
      name: formData.get('name') as string || undefined,
      sender_pattern: formData.get('sender_pattern') as string,
      subject_pattern: formData.get('subject_pattern') as string || undefined,
      template: formData.get('template') as string || 'generic',
      folder: formData.get('folder') as string || undefined,
    }

    if (editingParser) {
      updateParser.mutate({ id: editingParser.id, data })
    } else {
      createParser.mutate(data)
    }
  }

  const openEditParser = (parser: EmailParser) => {
    setEditingParser(parser)
    setSelectedBillId(parser.bill_id.toString())
    setIsParserOpen(true)
  }

  const closeParserDialog = () => {
    setIsParserOpen(false)
    setEditingParser(null)
    setSelectedBillId('')
    setIsCreatingBill(false)
    setNewBillName('')
  }

  const CategoryList = ({ categories, type }: { categories: Category[]; type: 'bill' | 'expense' }) => (
    <div className="space-y-2">
      {categories.map((cat) => (
        <div
          key={cat.id}
          className="flex items-center justify-between p-3 rounded-lg border"
        >
          <div className="flex items-center gap-3">
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: cat.color || '#6b7280' }}
            >
              <CategoryIcon name={cat.icon} className="h-4 w-4 text-white" fallback />

            </div>
            <div>
              <p className="font-medium">{cat.name}</p>
              {cat.budget_amount && (
                <p className="text-xs text-muted-foreground">
                  Budget: ${cat.budget_amount}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openEditCategory(cat)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => deleteCategory.mutate(cat.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      {categories.length === 0 && (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No {type} categories yet
        </p>
      )}
    </div>
  )

  const pendingEmails = parsedEmails.filter(e => e.status === 'pending')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue={searchParams.get('tab') || 'account'} className="space-y-4">
        <TabsList>
          <TabsTrigger value="account" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Account
          </TabsTrigger>
          <TabsTrigger value="household" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            Household
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Categories
          </TabsTrigger>
          {isHouseholdAdmin && (
            <>
              <TabsTrigger value="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
                {pendingEmails.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{pendingEmails.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="automation" className="flex items-center gap-2">
                <Timer className="h-4 w-4" />
                Automation
              </TabsTrigger>
              <TabsTrigger value="webhooks" className="flex items-center gap-2">
                <Webhook className="h-4 w-4" />
                Webhooks
              </TabsTrigger>
            </>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
              {pendingUsers.length > 0 && (
                <Badge variant="destructive" className="ml-1">{pendingUsers.length}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-md">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Role</Label>
                <p className="font-medium capitalize">{user?.role}</p>
              </div>
              <Button variant="destructive" onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Household Tab */}
        <TabsContent value="household">
          <HouseholdSettings />
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Categories</CardTitle>
                  <CardDescription>Organize bills and expenses</CardDescription>
                </div>
                <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Category</DialogTitle>
                      <DialogDescription>
                        Create a new category for organizing
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="type">Type</Label>
                        <Select
                          value={createType}
                          onValueChange={(v) => setCreateType(v as 'bill' | 'expense')}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bill">Bill</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" name="name" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="icon">Icon</Label>
                        <Select name="icon">
                          <SelectTrigger>
                            <SelectValue placeholder="Select icon" />
                          </SelectTrigger>
                          <SelectContent>
                            {ICONS.map((icon) => (
                              <SelectItem key={icon} value={icon}>
                                <span className="inline-flex items-center gap-2">
                                  <CategoryIcon name={icon} />
                                  {icon}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <div className="flex gap-2 flex-wrap">
                          {COLORS.map((color) => (
                            <label key={color} className="cursor-pointer">
                              <input
                                type="radio"
                                name="color"
                                value={color}
                                className="sr-only peer"
                              />
                              <div
                                className="h-8 w-8 rounded-full peer-checked:ring-2 ring-offset-2 ring-primary"
                                style={{ backgroundColor: color }}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget_amount">Budget (optional)</Label>
                        <Input
                          id="budget_amount"
                          name="budget_amount"
                          type="number"
                          step="0.01"
                          placeholder="Monthly budget"
                        />
                      </div>
                      <DialogFooter>
                        <Button type="submit" disabled={createCategory.isPending}>
                          {createCategory.isPending ? 'Creating...' : 'Create'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <h3 className="font-medium mb-3">Bill Categories ({billCategories.length})</h3>
                  <CategoryList categories={billCategories} type="bill" />
                </div>
                <div>
                  <h3 className="font-medium mb-3">Expense Categories ({expenseCategories.length})</h3>
                  <CategoryList categories={expenseCategories} type="expense" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Edit Category Dialog */}
          <Dialog open={isEditCategoryOpen} onOpenChange={setIsEditCategoryOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Category</DialogTitle>
                <DialogDescription>
                  Update category details
                </DialogDescription>
              </DialogHeader>
              {editingCategory && (
                <form onSubmit={handleEditCategorySubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Name</Label>
                    <Input
                      id="edit-name"
                      name="name"
                      defaultValue={editingCategory.name}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-icon">Icon</Label>
                    <Select name="icon" defaultValue={editingCategory.icon || undefined}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select icon" />
                      </SelectTrigger>
                      <SelectContent>
                        {ICONS.map((icon) => (
                          <SelectItem key={icon} value={icon}>
                            <span className="inline-flex items-center gap-2">
                              <CategoryIcon name={icon} />
                              {icon}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex gap-2 flex-wrap">
                      {COLORS.map((color) => (
                        <label key={color} className="cursor-pointer">
                          <input
                            type="radio"
                            name="color"
                            value={color}
                            defaultChecked={editingCategory.color === color}
                            className="sr-only peer"
                          />
                          <div
                            className="h-8 w-8 rounded-full peer-checked:ring-2 ring-offset-2 ring-primary"
                            style={{ backgroundColor: color }}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-budget">Budget (optional)</Label>
                    <Input
                      id="edit-budget"
                      name="budget_amount"
                      type="number"
                      step="0.01"
                      defaultValue={editingCategory.budget_amount || ''}
                      placeholder="Monthly budget"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={updateCategory.isPending}>
                      {updateCategory.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Email Tab (Household Admin Only) */}
        {isHouseholdAdmin && (
        <TabsContent value="email">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Config Card */}
            <Card>
              <CardHeader>
                <CardTitle>IMAP Configuration</CardTitle>
                <CardDescription>
                  Connect to your email to auto-extract bill data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEmailConfigSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="imap_host">IMAP Server</Label>
                    <Input
                      id="imap_host"
                      name="imap_host"
                      defaultValue={emailConfig?.imap_host || ''}
                      placeholder="imap.gmail.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="imap_port">Port</Label>
                    <Input
                      id="imap_port"
                      name="imap_port"
                      type="number"
                      defaultValue={emailConfig?.imap_port || 993}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="imap_user">Email Address</Label>
                    <Input
                      id="imap_user"
                      name="imap_user"
                      type="email"
                      defaultValue={emailConfig?.imap_user || ''}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="imap_password">
                      Password / App Password
                      {emailConfig?.has_password && (
                        <span className="text-muted-foreground ml-2">(saved)</span>
                      )}
                    </Label>
                    <Input
                      id="imap_password"
                      name="imap_password"
                      type="password"
                      placeholder={emailConfig?.has_password ? '••••••••' : 'Enter password'}
                      required={!emailConfig?.has_password}
                    />
                    <p className="text-xs text-muted-foreground">
                      {emailConfig?.has_password
                        ? 'Leave blank to keep existing password'
                        : 'Required - For Gmail, use an App Password'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={updateEmailConfig.isPending}>
                      {updateEmailConfig.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    {emailConfig?.has_password && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => testEmailConnection.mutate()}
                        disabled={testEmailConnection.isPending}
                      >
                        {testEmailConnection.isPending ? 'Testing...' : 'Test'}
                      </Button>
                    )}
                  </div>
                  {updateEmailConfig.isSuccess && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Configuration saved!
                    </div>
                  )}
                  {updateEmailConfig.isError && (
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      {(updateEmailConfig.error as any)?.response?.data?.error ||
                       (updateEmailConfig.error as Error)?.message ||
                       'Failed to save'}
                    </div>
                  )}
                  {testEmailConnection.isSuccess && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Connection successful!
                    </div>
                  )}
                  {testEmailConnection.isError && (
                    <div className="flex items-start gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {(testEmailConnection.error as any)?.response?.data?.error ||
                         (testEmailConnection.error as Error)?.message ||
                         'Connection failed'}
                      </span>
                    </div>
                  )}
                  {emailConfig?.last_check && (
                    <p className="text-xs text-muted-foreground">
                      Last checked: {new Date(emailConfig.last_check).toLocaleString()}
                    </p>
                  )}
                </form>

                {/* Microsoft OAuth Section */}
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium mb-2">Microsoft / Outlook</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Microsoft accounts require OAuth2 authentication (app passwords don't work).
                  </p>

                  {oauthMessage && (
                    <div className={`flex items-center gap-2 mb-4 ${oauthMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {oauthMessage.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                      <span className="text-sm">{oauthMessage.text}</span>
                    </div>
                  )}

                  {emailConfig?.oauth_connected ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-sm">Connected as {emailConfig.oauth_email}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => disconnectOAuth.mutate()}
                        disabled={disconnectOAuth.isPending}
                      >
                        {disconnectOAuth.isPending ? 'Disconnecting...' : 'Disconnect Microsoft'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => {
                        window.location.href = '/api/email/oauth/start'
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Connect with Microsoft
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Parser Rules Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Parser Rules</CardTitle>
                    <CardDescription>Match emails to bills</CardDescription>
                  </div>
                  <Dialog open={isParserOpen} onOpenChange={(open) => {
                    if (!open) closeParserDialog()
                    else setIsParserOpen(true)
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Rule
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingParser ? 'Edit' : 'Add'} Email Parser</DialogTitle>
                        <DialogDescription>
                          {editingParser ? 'Update the parser rule' : 'Create a rule to extract data from bill emails'}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleParserSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="bill_id">Bill</Label>
                          {isCreatingBill ? (
                            <div className="flex gap-2">
                              <Input
                                value={newBillName}
                                onChange={(e) => setNewBillName(e.target.value)}
                                placeholder="New bill name"
                                autoFocus
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  if (newBillName.trim()) {
                                    createBillForParser.mutate({
                                      name: newBillName.trim(),
                                      amount: 0,
                                      due_day: 1,
                                      frequency: 'monthly',
                                    })
                                  }
                                }}
                                disabled={!newBillName.trim() || createBillForParser.isPending}
                              >
                                {createBillForParser.isPending ? '...' : <Check className="h-4 w-4" />}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setIsCreatingBill(false)
                                  setNewBillName('')
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Select
                              value={selectedBillId}
                              onValueChange={(value) => {
                                if (value === 'new') {
                                  setIsCreatingBill(true)
                                } else {
                                  setSelectedBillId(value)
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select bill" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new" className="text-primary font-medium">
                                  <span className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    Create new bill...
                                  </span>
                                </SelectItem>
                                {bills.map((bill) => (
                                  <SelectItem key={bill.id} value={bill.id.toString()}>
                                    {bill.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="parser_name">Rule Name (optional)</Label>
                          <Input
                            id="parser_name"
                            name="name"
                            placeholder="e.g., Electric Bill Email"
                            defaultValue={editingParser?.name || ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sender_pattern">Sender Pattern</Label>
                          <Input
                            id="sender_pattern"
                            name="sender_pattern"
                            placeholder="e.g., noreply@electric.com"
                            defaultValue={editingParser?.sender_pattern || ''}
                            required
                          />
                          <p className="text-xs text-muted-foreground">
                            Supports regex patterns (e.g., "ugi.com" matches any @*.ugi.com)
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject_pattern">Subject Pattern (optional)</Label>
                          <Input
                            id="subject_pattern"
                            name="subject_pattern"
                            placeholder="e.g., Your.*bill is ready"
                            defaultValue={editingParser?.subject_pattern || ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="template">Parser Template</Label>
                          <Select name="template" defaultValue={editingParser?.template || 'generic'}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {emailTemplates.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="folder">Email Folder (optional)</Label>
                          <Input
                            id="folder"
                            name="folder"
                            placeholder="INBOX"
                            defaultValue={editingParser?.folder || ''}
                          />
                          <p className="text-xs text-muted-foreground">
                            Leave blank for INBOX. Use folder name if emails are filtered (e.g., "Bills")
                          </p>
                        </div>
                        <DialogFooter>
                          <Button
                            type="submit"
                            disabled={createParser.isPending || updateParser.isPending || !selectedBillId}
                          >
                            {(createParser.isPending || updateParser.isPending)
                              ? 'Saving...'
                              : editingParser ? 'Save Changes' : 'Create'}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {emailParsers.map((parser) => (
                    <div
                      key={parser.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{parser.name || parser.bill_name}</p>
                          <Badge variant="outline" className="text-xs">
                            {parser.template}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {parser.sender_pattern}
                        </p>
                        {parser.subject_pattern && (
                          <p className="text-xs text-muted-foreground truncate">
                            Subject: {parser.subject_pattern}
                          </p>
                        )}
                        {parser.folder && parser.folder !== 'INBOX' && (
                          <p className="text-xs text-muted-foreground truncate">
                            Folder: {parser.folder}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditParser(parser)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteParser.mutate(parser.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {emailParsers.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No parser rules yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Parsed Emails Card - Full Width */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Parsed Emails
                      {pendingEmails.length > 0 && (
                        <Badge variant="secondary">{pendingEmails.length} pending</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>Review and apply extracted data</CardDescription>
                  </div>
                  {(emailConfig?.has_password || emailConfig?.oauth_connected) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => checkEmails.mutate()}
                      disabled={checkEmails.isPending}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${checkEmails.isPending ? 'animate-spin' : ''}`} />
                      Check Now
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {parsedEmails.map((email) => (
                    <div
                      key={email.id}
                      className={`p-3 rounded-lg border ${
                        email.status === 'pending'
                          ? 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800'
                          : email.status === 'applied'
                          ? 'border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800'
                          : email.status === 'no_data'
                          ? 'border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800'
                          : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm truncate">{email.subject}</p>
                            <Badge
                              variant={
                                email.status === 'pending'
                                  ? 'default'
                                  : email.status === 'applied'
                                  ? 'secondary'
                                  : 'outline'
                              }
                              className="text-xs"
                            >
                              {email.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {email.sender} · {new Date(email.received_at).toLocaleDateString()}
                          </p>
                          <div className="flex gap-4 mt-1 text-sm">
                            {email.extracted_amount && (
                              <span className="text-green-600 dark:text-green-400">
                                ${email.extracted_amount.toFixed(2)}
                              </span>
                            )}
                            {email.extracted_due_date && (
                              <span className="text-blue-600 dark:text-blue-400">
                                Due: {new Date(email.extracted_due_date).toLocaleDateString()}
                              </span>
                            )}
                            {email.bill_name && (
                              <span className="text-muted-foreground">
                                → {email.bill_name}
                              </span>
                            )}
                            {email.extracted_payment_link && (
                              <a
                                href={email.extracted_payment_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Pay
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {email.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => applyParsedEmail.mutate(email.id)}
                              disabled={applyParsedEmail.isPending}
                              title="Apply to bill"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          {(email.status === 'pending' || email.status === 'no_data') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => ignoreParsedEmail.mutate(email.id)}
                              disabled={ignoreParsedEmail.isPending}
                              title="Ignore"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteParsedEmail.mutate(email.id)}
                            disabled={deleteParsedEmail.isPending}
                            title="Delete (allows re-parsing)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {parsedEmails.length === 0 && (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      No parsed emails yet. Configure IMAP, add parser rules, then click "Check Now".
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}

        {/* Automation Tab (Household Admin Only) */}
        {isHouseholdAdmin && (
        <TabsContent value="automation">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Email Automation Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                      <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Email Parser</CardTitle>
                      <CardDescription>Auto-check for bill emails</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="email-enabled"
                      checked={schedulerStatus?.email.enabled || false}
                      onChange={(e) => updateScheduler.mutate({
                        type: 'email',
                        enabled: e.target.checked,
                        interval: schedulerStatus?.email.interval,
                      })}
                      className="h-5 w-5 rounded border-gray-300"
                    />
                    <label htmlFor="email-enabled" className="text-sm font-medium">
                      {schedulerStatus?.email.enabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-interval">Check every</Label>
                  <Select
                    value={String(schedulerStatus?.email.interval || 60)}
                    onValueChange={(v) => updateScheduler.mutate({
                      type: 'email',
                      enabled: schedulerStatus?.email.enabled || false,
                      interval: parseInt(v),
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="360">6 hours</SelectItem>
                      <SelectItem value="720">12 hours</SelectItem>
                      <SelectItem value="1440">24 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Last run:</span>
                    <span className="text-sm">
                      {schedulerStatus?.email.lastRun?.time
                        ? new Date(schedulerStatus.email.lastRun.time).toLocaleString()
                        : 'Never'}
                    </span>
                  </div>
                  {schedulerStatus?.email.lastRun?.time && (
                    <div className="flex items-center gap-2 text-sm">
                      {schedulerStatus.email.lastRun?.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      <span className={schedulerStatus.email.lastRun?.success ? 'text-green-600' : 'text-red-600'}>
                        {schedulerStatus.email.lastRun?.success
                          ? `Found ${schedulerStatus.email.lastRun?.result?.processed || 0} emails`
                          : schedulerStatus.email.lastRun?.error}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => runSchedulerJob.mutate('email')}
                  disabled={runSchedulerJob.isPending}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Run Now
                </Button>
              </CardContent>
            </Card>

            {/* Transaction Sync Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                      <CreditCard className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Transaction Sync</CardTitle>
                      <CardDescription>Auto-sync from SimpleFIN</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="transactions-enabled"
                      checked={schedulerStatus?.transactions.enabled || false}
                      onChange={(e) => updateScheduler.mutate({
                        type: 'transactions',
                        enabled: e.target.checked,
                        interval: schedulerStatus?.transactions.interval,
                      })}
                      className="h-5 w-5 rounded border-gray-300"
                    />
                    <label htmlFor="transactions-enabled" className="text-sm font-medium">
                      {schedulerStatus?.transactions.enabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="transactions-interval">Sync every</Label>
                  <Select
                    value={String(schedulerStatus?.transactions.interval || 360)}
                    onValueChange={(v) => updateScheduler.mutate({
                      type: 'transactions',
                      enabled: schedulerStatus?.transactions.enabled || false,
                      interval: parseInt(v),
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="360">6 hours</SelectItem>
                      <SelectItem value="720">12 hours</SelectItem>
                      <SelectItem value="1440">24 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Last run:</span>
                    <span className="text-sm">
                      {schedulerStatus?.transactions.lastRun?.time
                        ? new Date(schedulerStatus.transactions.lastRun.time).toLocaleString()
                        : 'Never'}
                    </span>
                  </div>
                  {schedulerStatus?.transactions.lastRun?.time && (
                    <div className="flex items-center gap-2 text-sm">
                      {schedulerStatus.transactions.lastRun?.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      <span className={schedulerStatus.transactions.lastRun?.success ? 'text-green-600' : 'text-red-600'}>
                        {schedulerStatus.transactions.lastRun?.success
                          ? `Synced ${schedulerStatus.transactions.lastRun?.result?.transactions?.created || 0} transactions`
                          : schedulerStatus.transactions.lastRun?.error}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => runSchedulerJob.mutate('transactions')}
                  disabled={runSchedulerJob.isPending}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Run Now
                </Button>
              </CardContent>
            </Card>

            {/* Email Reminders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Email Reminders
                </CardTitle>
                <CardDescription>
                  Send email reminders via SendGrid for upcoming and overdue bills.
                  Requires SENDGRID_API_KEY to be configured on the server.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {remindersLoading ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : reminderPrefs ? (
                  <>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="reminders-enabled">Enable Email Reminders</Label>
                      <input
                        id="reminders-enabled"
                        type="checkbox"
                        checked={reminderPrefs.enabled}
                        onChange={(e) =>
                          updateReminderPrefsMutation.mutate({ enabled: e.target.checked })
                        }
                        className="h-4 w-4"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Send reminder this many days before due date:</Label>
                      <div className="flex gap-4 flex-wrap">
                        {[1, 3, 7, 14].map((day) => (
                          <label key={day} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={reminderPrefs.days_before.includes(day)}
                              disabled={!reminderPrefs.enabled}
                              onChange={(e) => {
                                const current = reminderPrefs.days_before
                                const next = e.target.checked
                                  ? [...current, day].sort((a, b) => a - b)
                                  : current.filter((d) => d !== day)
                                updateReminderPrefsMutation.mutate({ days_before: next })
                              }}
                            />
                            {day} {day === 1 ? 'day' : 'days'}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label htmlFor="reminders-overdue">Send reminders for overdue bills</Label>
                      <input
                        id="reminders-overdue"
                        type="checkbox"
                        checked={reminderPrefs.send_overdue}
                        disabled={!reminderPrefs.enabled}
                        onChange={(e) =>
                          updateReminderPrefsMutation.mutate({ send_overdue: e.target.checked })
                        }
                        className="h-4 w-4"
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Could not load reminder preferences.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}

        {/* Webhooks Tab (Household Admin Only) */}
        {isHouseholdAdmin && (
        <TabsContent value="webhooks">
          <WebhooksSection />
        </TabsContent>
        )}

        {/* Users Tab (Admin Only) */}
        {user?.role === 'admin' && (
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Pending Approvals Card */}
              {pendingUsers.length > 0 && (
                <Card className="border-yellow-200 dark:border-yellow-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      Pending Approvals ({pendingUsers.length})
                    </CardTitle>
                    <CardDescription>
                      These users are waiting for account approval
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pendingUsers.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-950"
                        >
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Registered {new Date(u.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveUser.mutate(u.id)}
                              disabled={approveUser.isPending}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => rejectUser.mutate(u.id)}
                              disabled={rejectUser.isPending}
                            >
                              <UserX className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All Users Card */}
              <Card>
                <CardHeader>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>Manage user accounts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {allUsers.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                            {u.role === 'admin' ? (
                              <Shield className="h-5 w-5 text-primary" />
                            ) : (
                              <User className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{u.name}</p>
                              <Badge
                                variant={
                                  u.status === 'approved'
                                    ? 'secondary'
                                    : u.status === 'pending'
                                    ? 'default'
                                    : 'destructive'
                                }
                                className="text-xs"
                              >
                                {u.status}
                              </Badge>
                              {u.role === 'admin' && (
                                <Badge variant="outline" className="text-xs">
                                  Admin
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {u.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => approveUser.mutate(u.id)}
                                disabled={approveUser.isPending}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rejectUser.mutate(u.id)}
                                disabled={rejectUser.isPending}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {u.id !== user?.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (confirm(`Are you sure you want to delete ${u.name}?`)) {
                                  deleteUser.mutate(u.id)
                                }
                              }}
                              disabled={deleteUser.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {allUsers.length === 0 && (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No users found
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

// Webhooks Section Component
function WebhooksSection() {
  const queryClient = useQueryClient()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [newWebhook, setNewWebhook] = useState({ name: '', url: '', events: [] as string[] })
  const [createdSecret, setCreatedSecret] = useState<string | null>(null)

  const { data: webhooks = [] } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const res = await webhooksApi.getAll()
      return res.data
    },
  })

  const { data: eventTypes = [] } = useQuery({
    queryKey: ['webhook-events'],
    queryFn: async () => {
      const res = await webhooksApi.getEvents()
      return res.data as { id: string; name: string }[]
    },
  })

  const createWebhook = useMutation({
    mutationFn: (data: { name: string; url: string; events: string[] }) =>
      webhooksApi.create(data),
    onSuccess: (res) => {
      setCreatedSecret(res.data.secret)
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })

  const deleteWebhook = useMutation({
    mutationFn: webhooksApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })

  const testWebhook = useMutation({
    mutationFn: webhooksApi.test,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })

  const toggleEvent = (eventId: string) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }))
  }

  const handleCreate = () => {
    createWebhook.mutate(newWebhook)
  }

  const handleCloseCreate = () => {
    setIsCreateOpen(false)
    setNewWebhook({ name: '', url: '', events: [] })
    setCreatedSecret(null)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhooks
              </CardTitle>
              <CardDescription>
                Send notifications to external services when events occur
              </CardDescription>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Webhook
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Webhook</DialogTitle>
                  <DialogDescription>
                    Configure a webhook to receive notifications
                  </DialogDescription>
                </DialogHeader>
                {createdSecret ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                      <p className="font-medium text-green-700 dark:text-green-400 mb-2">
                        Webhook created! Save your secret:
                      </p>
                      <code className="block p-2 bg-white dark:bg-gray-900 rounded text-sm break-all">
                        {createdSecret}
                      </code>
                      <p className="text-xs text-muted-foreground mt-2">
                        This secret will not be shown again. Use it to verify webhook signatures.
                      </p>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleCloseCreate}>Done</Button>
                    </DialogFooter>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={newWebhook.name}
                        onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="My Webhook"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input
                        value={newWebhook.url}
                        onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://example.com/webhook"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Events</Label>
                      <div className="flex flex-wrap gap-2">
                        {eventTypes.map(event => (
                          <Badge
                            key={event.id}
                            variant={newWebhook.events.includes(event.id) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleEvent(event.id)}
                          >
                            {event.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={handleCloseCreate}>Cancel</Button>
                      <Button
                        onClick={handleCreate}
                        disabled={!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0 || createWebhook.isPending}
                      >
                        {createWebhook.isPending ? 'Creating...' : 'Create'}
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {webhooks.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No webhooks configured. Create one to receive notifications.
            </p>
          ) : (
            <div className="space-y-4">
              {webhooks.map((webhook: any) => (
                <div key={webhook.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{webhook.name}</p>
                      <Badge variant={webhook.active ? 'secondary' : 'outline'}>
                        {webhook.active ? 'Active' : 'Inactive'}
                      </Badge>
                      {webhook.last_status && (
                        <Badge variant={webhook.last_status < 400 ? 'secondary' : 'destructive'}>
                          {webhook.last_status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate max-w-md">{webhook.url}</p>
                    <div className="flex gap-1 mt-1">
                      {webhook.events?.map((event: string) => (
                        <Badge key={event} variant="outline" className="text-xs">
                          {event}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => testWebhook.mutate(webhook.id)}
                      disabled={testWebhook.isPending}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteWebhook.mutate(webhook.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
